import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { calculateNutrientRecommendations, canCalculateRecommendations } from '../utils/nutrient-calculator';

interface ClientProfilePageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
}

interface Supplement {
  id?: string;
  name: string;
  dosage: string;
  unit: string;
  frequency: 'daily' | 'weekly' | 'as_needed';
  time_of_day: string;
  reminder_enabled: boolean;
  notes?: string;
  nutrients?: {
    [key: string]: number;
  };
  product_url?: string;
  purchase_url?: string;
  created_by?: 'user' | 'coach';
  coach_notes?: string;
}

interface ProfileData {
  // Personal Information
  full_name: string;
  email: string;
  gender: string;
  age: number | string;
  height: number | string;
  weight: number | string;
  activity_level: string;

  // Health & Dietary Information
  primary_focus: string;
  dietary_restrictions: string;
  allergies: string;

  // App Preferences
  preferred_method: string;
  experience_level: string;

  // Macronutrient Recommendations
  daily_calorie_rec: number | string;
  protein_rec: number | string;
  carbs_rec: number | string;
  fat_rec: number | string;
  fiber_rec: number | string;

  // Vitamin Recommendations
  vitamin_a_rec: number | string;
  vitamin_c_rec: number | string;
  vitamin_d_rec: number | string;
  vitamin_e_rec: number | string;
  vitamin_k_rec: number | string;
  thiamin_rec: number | string;
  riboflavin_rec: number | string;
  niacin_rec: number | string;
  vitamin_b6_rec: number | string;
  folate_rec: number | string;
  vitamin_b12_rec: number | string;

  // Mineral Recommendations
  calcium_rec: number | string;
  iron_rec: number | string;
  magnesium_rec: number | string;
  phosphorus_rec: number | string;
  potassium_rec: number | string;
  sodium_rec: number | string;
  zinc_rec: number | string;
  selenium_rec: number | string;

  // Essential Fatty Acids
  omega3_rec: number | string;
  omega6_rec: number | string;

  // Essential Amino Acid Recommendations
  lysine_rec: number | string;
  leucine_rec: number | string;
  isoleucine_rec: number | string;
  phenylalanine_rec: number | string;
  methionine_rec: number | string;
  valine_rec: number | string;
  threonine_rec: number | string;
  tryptophan_rec: number | string;
  histidine_rec: number | string;
}

export const ClientProfilePage: React.FC<ClientProfilePageProps> = ({
  user,
  coachMode = false,
  clientId
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'personal' | 'recommendations' | 'supplements' | 'preferences'>('personal');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingRecommendations, setPendingRecommendations] = useState<any>(null);
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');

  // Supplements state
  const [supplements, setSupplements] = useState<Supplement[]>([]);
  const [showSupplementModal, setShowSupplementModal] = useState(false);
  const [editingSupplement, setEditingSupplement] = useState<Supplement | null>(null);

  const [profileData, setProfileData] = useState<ProfileData>({
    full_name: '',
    email: '',
    gender: '',
    age: '',
    height: '',
    weight: '',
    activity_level: '',
    primary_focus: '',
    dietary_restrictions: '',
    allergies: '',
    preferred_method: '',
    experience_level: '',
    // Macronutrients
    daily_calorie_rec: '',
    protein_rec: '',
    carbs_rec: '',
    fat_rec: '',
    fiber_rec: '',
    // Vitamins
    vitamin_a_rec: '',
    vitamin_c_rec: '',
    vitamin_d_rec: '',
    vitamin_e_rec: '',
    vitamin_k_rec: '',
    thiamin_rec: '',
    riboflavin_rec: '',
    niacin_rec: '',
    vitamin_b6_rec: '',
    folate_rec: '',
    vitamin_b12_rec: '',
    // Minerals
    calcium_rec: '',
    iron_rec: '',
    magnesium_rec: '',
    phosphorus_rec: '',
    potassium_rec: '',
    sodium_rec: '',
    zinc_rec: '',
    selenium_rec: '',
    // Essential Fatty Acids
    omega3_rec: '',
    omega6_rec: '',
    // Amino Acids
    lysine_rec: '',
    leucine_rec: '',
    isoleucine_rec: '',
    phenylalanine_rec: '',
    methionine_rec: '',
    valine_rec: '',
    threonine_rec: '',
    tryptophan_rec: '',
    histidine_rec: ''
  });

  // Get CSRF token on mount
  useEffect(() => {
    const getCSRFToken = async () => {
      try {
        // Generate or get session ID
        let sid = sessionStorage.getItem('session_id');
        if (!sid) {
          sid = Math.random().toString(36).substring(2) + Date.now().toString(36);
          sessionStorage.setItem('session_id', sid);
        }
        setSessionId(sid);

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/csrf-token`, {
          headers: {
            'X-Session-Id': sid,
          },
          credentials: 'include'
        });

        const data = await response.json();
        console.log('CSRF token response:', data);
        if (data.success && (data.csrfToken || data.csrf_token)) {
          const token = data.csrfToken || data.csrf_token;
          setCsrfToken(token);
          console.log('CSRF token set:', token);
        } else {
          console.error('Failed to get CSRF token:', data);
        }
      } catch (error) {
        console.error('Failed to get CSRF token:', error);
      }
    };

    getCSRFToken();
  }, []);

  // Load client profile data
  useEffect(() => {
    const loadProfileData = async () => {
      if (!clientId && !user) return;

      try {
        setIsLoading(true);

        // Use coach-specific endpoint if in coach mode
        const endpoint = coachMode && clientId
          ? `/api/coach/clients/${clientId}/profile`
          : '/api/profile';

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });

        const data = await response.json();

        if (data.success && data.profile) {
          setProfileData({
            full_name: data.profile.full_name || user?.full_name || '',
            email: data.profile.email || user?.email || '',
            gender: data.profile.gender || '',
            age: data.profile.age || '',
            height: data.profile.height || '',
            weight: data.profile.weight || '',
            activity_level: data.profile.activity_level || '',
            primary_focus: data.profile.primary_focus || data.profile.primary_goal || '',
            dietary_restrictions: data.profile.dietary_restrictions || '',
            allergies: data.profile.allergies || '',
            preferred_method: data.profile.preferred_method || '',
            experience_level: data.profile.experience_level || '',
            // Macronutrients
            daily_calorie_rec: data.profile.daily_calorie_rec || data.profile.daily_calorie_goal || '',
            protein_rec: data.profile.protein_rec || data.profile.protein_goal || '',
            carbs_rec: data.profile.carbs_rec || data.profile.carbs_goal || '',
            fat_rec: data.profile.fat_rec || data.profile.fat_goal || '',
            fiber_rec: data.profile.fiber_rec || data.profile.fiber_goal || '',
            // Vitamins
            vitamin_a_rec: data.profile.vitamin_a_rec || '',
            vitamin_c_rec: data.profile.vitamin_c_rec || '',
            vitamin_d_rec: data.profile.vitamin_d_rec || '',
            vitamin_e_rec: data.profile.vitamin_e_rec || '',
            vitamin_k_rec: data.profile.vitamin_k_rec || '',
            thiamin_rec: data.profile.thiamin_rec || '',
            riboflavin_rec: data.profile.riboflavin_rec || '',
            niacin_rec: data.profile.niacin_rec || '',
            vitamin_b6_rec: data.profile.vitamin_b6_rec || '',
            folate_rec: data.profile.folate_rec || '',
            vitamin_b12_rec: data.profile.vitamin_b12_rec || '',
            // Minerals
            calcium_rec: data.profile.calcium_rec || '',
            iron_rec: data.profile.iron_rec || '',
            magnesium_rec: data.profile.magnesium_rec || '',
            phosphorus_rec: data.profile.phosphorus_rec || '',
            potassium_rec: data.profile.potassium_rec || '',
            sodium_rec: data.profile.sodium_rec || '',
            zinc_rec: data.profile.zinc_rec || '',
            selenium_rec: data.profile.selenium_rec || '',
            // Essential Fatty Acids
            omega3_rec: data.profile.omega3_rec || '',
            omega6_rec: data.profile.omega6_rec || '',
            // Essential Amino Acids
            lysine_rec: data.profile.lysine_rec || '',
            leucine_rec: data.profile.leucine_rec || '',
            isoleucine_rec: data.profile.isoleucine_rec || '',
            phenylalanine_rec: data.profile.phenylalanine_rec || '',
            methionine_rec: data.profile.methionine_rec || '',
            valine_rec: data.profile.valine_rec || '',
            threonine_rec: data.profile.threonine_rec || '',
            tryptophan_rec: data.profile.tryptophan_rec || '',
            histidine_rec: data.profile.histidine_rec || ''
          });
        }
      } catch (error) {
        console.error('Failed to load profile data:', error);
        setMessage('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [clientId, user, coachMode]);

  const handleInputChange = (field: keyof ProfileData, value: string | number) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const endpoint = coachMode && clientId
        ? `/api/coach/clients/${clientId}/profile`
        : '/api/profile';

      console.log('Saving profile to:', `${process.env.REACT_APP_API_BASE_URL}${endpoint}`);
      console.log('Profile data:', profileData);
      console.log('CSRF token:', csrfToken);
      console.log('Session ID:', sessionId);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Session-Id': sessionId,
        },
        credentials: 'include',
        body: JSON.stringify(profileData)
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        setMessage('✅ Profile updated successfully! Changes saved to database.');
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage('❌ Failed to update profile: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      setMessage('❌ Network error - Failed to save profile. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  // Calculate BMR
  const calculateBMR = () => {
    const age = Number(profileData.age);
    const height = Number(profileData.height);
    const weight = Number(profileData.weight);

    if (!age || !height || !weight) return 0;

    if (profileData.gender === 'male') {
      return Math.round(88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age));
    } else {
      return Math.round(447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age));
    }
  };

  // Calculate TDEE
  const calculateTDEE = () => {
    const bmr = calculateBMR();
    if (!bmr) return 0;

    const multipliers: Record<string, number> = {
      sedentary: 1.2,
      lightly_active: 1.375,
      moderately_active: 1.55,
      very_active: 1.725,
      extremely_active: 1.9
    };
    return Math.round(bmr * (multipliers[profileData.activity_level] || 1.55));
  };

  // Handle calculate recommendations
  const handleCalculateRecommendations = () => {
    const profile = {
      gender: profileData.gender,
      age: Number(profileData.age),
      weight: Number(profileData.weight),
      height: Number(profileData.height),
      activity_level: profileData.activity_level,
      primary_focus: profileData.primary_focus
    };

    if (!canCalculateRecommendations(profile)) {
      setMessage('❌ Please fill in: gender, age, weight, height, and activity level first');
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // Calculate recommendations
    const recommendations = calculateNutrientRecommendations(profile);

    // Store pending recommendations and show confirmation modal
    setPendingRecommendations(recommendations);
    setShowConfirmModal(true);
  };

  // Apply calculated recommendations after confirmation
  const handleConfirmRecommendations = () => {
    if (!pendingRecommendations) return;

    // Update profile data with calculated values
    setProfileData(prev => ({
      ...prev,
      daily_calorie_rec: pendingRecommendations.daily_calorie_rec,
      protein_rec: pendingRecommendations.protein_rec,
      carbs_rec: pendingRecommendations.carbs_rec,
      fat_rec: pendingRecommendations.fat_rec,
      fiber_rec: pendingRecommendations.fiber_rec,
      omega3_rec: pendingRecommendations.omega3_rec,
      omega6_rec: pendingRecommendations.omega6_rec,
      vitamin_a_rec: pendingRecommendations.vitamin_a_rec,
      vitamin_c_rec: pendingRecommendations.vitamin_c_rec,
      vitamin_d_rec: pendingRecommendations.vitamin_d_rec,
      vitamin_e_rec: pendingRecommendations.vitamin_e_rec,
      vitamin_k_rec: pendingRecommendations.vitamin_k_rec,
      thiamin_rec: pendingRecommendations.thiamin_rec,
      riboflavin_rec: pendingRecommendations.riboflavin_rec,
      niacin_rec: pendingRecommendations.niacin_rec,
      vitamin_b6_rec: pendingRecommendations.vitamin_b6_rec,
      folate_rec: pendingRecommendations.folate_rec,
      vitamin_b12_rec: pendingRecommendations.vitamin_b12_rec,
      calcium_rec: pendingRecommendations.calcium_rec,
      iron_rec: pendingRecommendations.iron_rec,
      magnesium_rec: pendingRecommendations.magnesium_rec,
      phosphorus_rec: pendingRecommendations.phosphorus_rec,
      potassium_rec: pendingRecommendations.potassium_rec,
      sodium_rec: pendingRecommendations.sodium_rec,
      zinc_rec: pendingRecommendations.zinc_rec,
      selenium_rec: pendingRecommendations.selenium_rec,
      lysine_rec: pendingRecommendations.lysine_rec,
      leucine_rec: pendingRecommendations.leucine_rec,
      isoleucine_rec: pendingRecommendations.isoleucine_rec,
      phenylalanine_rec: pendingRecommendations.phenylalanine_rec,
      methionine_rec: pendingRecommendations.methionine_rec,
      valine_rec: pendingRecommendations.valine_rec,
      threonine_rec: pendingRecommendations.threonine_rec,
      tryptophan_rec: pendingRecommendations.tryptophan_rec,
      histidine_rec: pendingRecommendations.histidine_rec
    }));

    setShowConfirmModal(false);
    setPendingRecommendations(null);
    setMessage('✅ Recommendations applied! Review and click "Save Changes" to update the database.');
    setTimeout(() => setMessage(null), 5000);
  };

  // Cancel recommendation calculation
  const handleCancelRecommendations = () => {
    setShowConfirmModal(false);
    setPendingRecommendations(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {coachMode ? 'Client Profile' : 'My Profile'}
            </h1>
            <p className="text-gray-600 mt-1">
              {coachMode
                ? `Managing profile for ${user?.full_name || 'client'}`
                : 'Update your personal information and nutrition goals'
              }
            </p>
          </div>
          {coachMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-blue-700">
                👨‍⚕️ <strong>Coach View</strong> - Editing on behalf of client
              </p>
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('✅')
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message}
        </div>
      )}

      {/* Section Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-4">
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'personal'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
            }`}
            onClick={() => setActiveSection('personal')}
          >
            📋 Personal Info
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'recommendations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
            }`}
            onClick={() => setActiveSection('recommendations')}
          >
            💡 Recommendations
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'supplements'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
            }`}
            onClick={() => setActiveSection('supplements')}
          >
            💊 Supplements
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'preferences'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
            }`}
            onClick={() => setActiveSection('preferences')}
          >
            ⚙️ Preferences
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Personal Information Section */}
        {activeSection === 'personal' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">📋 Personal Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email"
                  disabled={coachMode}
                  title={coachMode ? "Email cannot be changed by coach" : ""}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  value={profileData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age
                </label>
                <input
                  type="number"
                  value={profileData.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter age"
                  min="13"
                  max="120"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Height (cm)
                </label>
                <input
                  type="number"
                  value={profileData.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter height in cm"
                  min="100"
                  max="250"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  value={profileData.weight}
                  onChange={(e) => handleInputChange('weight', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter weight in kg"
                  min="20"
                  max="500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Activity Level
                </label>
                <select
                  value={profileData.activity_level}
                  onChange={(e) => handleInputChange('activity_level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select activity level</option>
                  <option value="sedentary">Sedentary (little/no exercise)</option>
                  <option value="lightly_active">Lightly active (light exercise 1-3 days/week)</option>
                  <option value="moderately_active">Moderately active (moderate exercise 3-5 days/week)</option>
                  <option value="very_active">Very active (hard exercise 6-7 days/week)</option>
                  <option value="extremely_active">Extremely active (very hard exercise, physical job)</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Focus
                </label>
                <select
                  value={profileData.primary_focus}
                  onChange={(e) => handleInputChange('primary_focus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select your focus</option>
                  <option value="weight_loss">Weight Loss</option>
                  <option value="weight_gain">Weight Gain</option>
                  <option value="muscle_gain">Muscle Gain</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="health_improvement">Health Improvement</option>
                  <option value="sports_performance">Sports Performance</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dietary Approach
                </label>
                <input
                  type="text"
                  value={profileData.dietary_restrictions}
                  onChange={(e) => handleInputChange('dietary_restrictions', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. vegetarian, vegan, keto, mediterranean, etc."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allergies & Intolerances
                </label>
                <input
                  type="text"
                  value={profileData.allergies}
                  onChange={(e) => handleInputChange('allergies', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. nuts, dairy, gluten, shellfish, etc."
                />
              </div>
            </div>

            {/* Calculated Metrics */}
            {profileData.height && profileData.weight && profileData.age && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Calculated Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">🔥</div>
                      <div>
                        <div className="text-sm text-gray-600">Basal Metabolic Rate</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {calculateBMR()} <span className="text-sm font-normal">cal/day</span>
                        </div>
                        <div className="text-xs text-gray-500">Calories at rest</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">⚡</div>
                      <div>
                        <div className="text-sm text-gray-600">Total Daily Energy</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {calculateTDEE()} <span className="text-sm font-normal">cal/day</span>
                        </div>
                        <div className="text-xs text-gray-500">Including activity</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommendations Section */}
        {activeSection === 'recommendations' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">💡 Nutrition Recommendations</h2>
            <p className="text-gray-600 mb-6">
              Set personalized nutrient recommendations. These help track intake without creating pressure.
            </p>

            {/* Calculate Button */}
            <div className="mb-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start space-x-4">
                  <div className="text-3xl">💡</div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      How Recommendations Work
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-2 mb-4">
                      <li className="flex items-start">
                        <span className="mr-2">1️⃣</span>
                        <span><strong>Auto-Calculate:</strong> Click the button below to calculate science-based recommendations using your profile data</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">2️⃣</span>
                        <span><strong>Review:</strong> A preview modal will show all calculated values for your approval</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">3️⃣</span>
                        <span><strong>Customize:</strong> After applying, you can manually edit ANY field to set therapeutic or custom targets that override the baseline recommendations</span>
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2">4️⃣</span>
                        <span><strong>Save:</strong> Click "Save Changes" at the bottom to store your final values (whether calculated or custom)</span>
                      </li>
                    </ul>
                    <div className="text-center">
                      <button
                        onClick={handleCalculateRecommendations}
                        disabled={!profileData.gender || !profileData.age || !profileData.weight || !profileData.height || !profileData.activity_level}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md"
                      >
                        📊 Calculate Personalized Recommendations
                      </button>
                      <p className="text-xs text-gray-500 mt-2">
                        {(!profileData.gender || !profileData.age || !profileData.weight || !profileData.height || !profileData.activity_level)
                          ? '⚠️ Complete Personal Info tab first'
                          : 'Based on your gender, age, weight, height, activity level, and goals'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Macronutrients */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="mr-2">🍽️</span> Macronutrient Recommendations
                </h3>
                <div className="bg-green-50 border border-green-200 rounded px-3 py-1 text-xs text-green-700">
                  ✏️ All fields are editable - set custom therapeutic targets
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Daily Calories
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.daily_calorie_rec}
                      onChange={(e) => handleInputChange('daily_calorie_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 2000"
                      min="800"
                      max="5000"
                    />
                    <span className="ml-2 text-sm text-gray-600">kcal/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Protein
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.protein_rec}
                      onChange={(e) => handleInputChange('protein_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 120"
                      min="20"
                      max="300"
                    />
                    <span className="ml-2 text-sm text-gray-600">g/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Carbohydrates
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.carbs_rec}
                      onChange={(e) => handleInputChange('carbs_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 250"
                      min="20"
                      max="600"
                    />
                    <span className="ml-2 text-sm text-gray-600">g/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fat
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.fat_rec}
                      onChange={(e) => handleInputChange('fat_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 80"
                      min="20"
                      max="200"
                    />
                    <span className="ml-2 text-sm text-gray-600">g/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fibre
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.fiber_rec}
                      onChange={(e) => handleInputChange('fiber_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 25"
                      min="10"
                      max="60"
                    />
                    <span className="ml-2 text-sm text-gray-600">g/day</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vitamins */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🌟</span> Vitamin Recommendations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vitamin A
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.vitamin_a_rec}
                      onChange={(e) => handleInputChange('vitamin_a_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 900"
                    />
                    <span className="ml-2 text-sm text-gray-600">mcg RAE/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vitamin C
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.vitamin_c_rec}
                      onChange={(e) => handleInputChange('vitamin_c_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 90"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vitamin D
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.vitamin_d_rec}
                      onChange={(e) => handleInputChange('vitamin_d_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 20"
                    />
                    <span className="ml-2 text-sm text-gray-600">mcg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vitamin E
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.vitamin_e_rec}
                      onChange={(e) => handleInputChange('vitamin_e_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 15"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vitamin K
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.vitamin_k_rec}
                      onChange={(e) => handleInputChange('vitamin_k_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 120"
                    />
                    <span className="ml-2 text-sm text-gray-600">mcg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thiamin (B1)
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={profileData.thiamin_rec}
                      onChange={(e) => handleInputChange('thiamin_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1.2"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Riboflavin (B2)
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={profileData.riboflavin_rec}
                      onChange={(e) => handleInputChange('riboflavin_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1.3"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niacin (B3)
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.niacin_rec}
                      onChange={(e) => handleInputChange('niacin_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 16"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vitamin B6
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={profileData.vitamin_b6_rec}
                      onChange={(e) => handleInputChange('vitamin_b6_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1.7"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Folate (B9)
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.folate_rec}
                      onChange={(e) => handleInputChange('folate_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 400"
                    />
                    <span className="ml-2 text-sm text-gray-600">mcg DFE/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vitamin B12
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={profileData.vitamin_b12_rec}
                      onChange={(e) => handleInputChange('vitamin_b12_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 2.4"
                    />
                    <span className="ml-2 text-sm text-gray-600">mcg/day</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Minerals */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⚡</span> Mineral Recommendations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calcium
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.calcium_rec}
                      onChange={(e) => handleInputChange('calcium_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1000"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Iron
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.iron_rec}
                      onChange={(e) => handleInputChange('iron_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 18"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Magnesium
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.magnesium_rec}
                      onChange={(e) => handleInputChange('magnesium_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 400"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phosphorus
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.phosphorus_rec}
                      onChange={(e) => handleInputChange('phosphorus_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 700"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Potassium
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.potassium_rec}
                      onChange={(e) => handleInputChange('potassium_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 3500"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sodium
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.sodium_rec}
                      onChange={(e) => handleInputChange('sodium_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 2300"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zinc
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.zinc_rec}
                      onChange={(e) => handleInputChange('zinc_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 11"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selenium
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.selenium_rec}
                      onChange={(e) => handleInputChange('selenium_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 55"
                    />
                    <span className="ml-2 text-sm text-gray-600">mcg/day</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Essential Fatty Acids */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🥑</span> Essential Fatty Acid Recommendations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Omega-3
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={profileData.omega3_rec}
                      onChange={(e) => handleInputChange('omega3_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 3.1"
                    />
                    <span className="ml-2 text-sm text-gray-600">g/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Omega-6
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={profileData.omega6_rec}
                      onChange={(e) => handleInputChange('omega6_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 9.2"
                    />
                    <span className="ml-2 text-sm text-gray-600">g/day</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Essential Amino Acids */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🧬</span> Essential Amino Acid Recommendations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lysine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.lysine_rec}
                      onChange={(e) => handleInputChange('lysine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1056"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Leucine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.leucine_rec}
                      onChange={(e) => handleInputChange('leucine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1232"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Isoleucine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.isoleucine_rec}
                      onChange={(e) => handleInputChange('isoleucine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 880"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phenylalanine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.phenylalanine_rec}
                      onChange={(e) => handleInputChange('phenylalanine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1232"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Methionine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.methionine_rec}
                      onChange={(e) => handleInputChange('methionine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1144"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.valine_rec}
                      onChange={(e) => handleInputChange('valine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 880"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Threonine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.threonine_rec}
                      onChange={(e) => handleInputChange('threonine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 616"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tryptophan
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={profileData.tryptophan_rec}
                      onChange={(e) => handleInputChange('tryptophan_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 308"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Histidine
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={profileData.histidine_rec}
                      onChange={(e) => handleInputChange('histidine_rec', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 880"
                    />
                    <span className="ml-2 text-sm text-gray-600">mg/day</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculated Metrics */}
            {profileData.height && profileData.weight && profileData.age && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Your Calculated Metrics</h3>
                <p className="text-sm text-gray-600 mb-4">Based on your current profile information</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">🔥</div>
                      <div>
                        <div className="text-sm text-gray-600">Basal Metabolic Rate</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {calculateBMR()} <span className="text-sm font-normal">cal/day</span>
                        </div>
                        <div className="text-xs text-gray-500">Calories at rest</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                    <div className="flex items-center space-x-3">
                      <div className="text-3xl">⚡</div>
                      <div>
                        <div className="text-sm text-gray-600">Total Daily Energy</div>
                        <div className="text-2xl font-bold text-gray-900">
                          {calculateTDEE()} <span className="text-sm font-normal">cal/day</span>
                        </div>
                        <div className="text-xs text-gray-500">Including activity</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>💡 Tip:</strong> BMR is your baseline calorie needs at rest. TDEE includes your activity level and is your maintenance calories. Use these as starting points for your calorie recommendations.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Supplements Section - Placeholder */}
        {activeSection === 'supplements' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">💊 Supplements</h2>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🚧</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Coming Soon</h3>
              <p className="text-gray-600">
                Supplements management interface will be implemented next.
              </p>
            </div>
          </div>
        )}

        {/* Preferences Section - Placeholder */}
        {activeSection === 'preferences' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">⚙️ App Preferences</h2>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🚧</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Coming Soon</h3>
              <p className="text-gray-600">
                App preferences interface will be implemented next.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? '🔄 Saving...' : '💾 Save Changes'}
        </button>
      </div>

      {/* Confirmation Modal for Calculated Recommendations */}
      {showConfirmModal && pendingRecommendations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                📊 Review Calculated Recommendations
              </h2>
              <p className="text-gray-600 mb-6">
                Based on your profile, here are your personalized nutrient recommendations. Review them before applying.
              </p>

              {/* Macronutrients Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="mr-2">🍽️</span> Macronutrients
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Calories</div>
                    <div className="text-xl font-bold text-gray-900">{pendingRecommendations.daily_calorie_rec} <span className="text-sm font-normal">kcal/day</span></div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Protein</div>
                    <div className="text-xl font-bold text-gray-900">{pendingRecommendations.protein_rec} <span className="text-sm font-normal">g/day</span></div>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Carbs</div>
                    <div className="text-xl font-bold text-gray-900">{pendingRecommendations.carbs_rec} <span className="text-sm font-normal">g/day</span></div>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Fat</div>
                    <div className="text-xl font-bold text-gray-900">{pendingRecommendations.fat_rec} <span className="text-sm font-normal">g/day</span></div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Fibre</div>
                    <div className="text-xl font-bold text-gray-900">{pendingRecommendations.fiber_rec} <span className="text-sm font-normal">g/day</span></div>
                  </div>
                </div>
              </div>

              {/* Vitamins Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="mr-2">🌟</span> Vitamins
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div className="bg-gray-50 p-2 rounded">Vitamin A: <strong>{pendingRecommendations.vitamin_a_rec} mcg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Vitamin C: <strong>{pendingRecommendations.vitamin_c_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Vitamin D: <strong>{pendingRecommendations.vitamin_d_rec} IU</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Vitamin E: <strong>{pendingRecommendations.vitamin_e_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Vitamin K: <strong>{pendingRecommendations.vitamin_k_rec} mcg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Thiamin (B1): <strong>{pendingRecommendations.thiamin_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Riboflavin (B2): <strong>{pendingRecommendations.riboflavin_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Niacin (B3): <strong>{pendingRecommendations.niacin_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Vitamin B6: <strong>{pendingRecommendations.vitamin_b6_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Folate (B9): <strong>{pendingRecommendations.folate_rec} mcg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Vitamin B12: <strong>{pendingRecommendations.vitamin_b12_rec} mcg</strong></div>
                </div>
              </div>

              {/* Minerals Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="mr-2">⚡</span> Minerals
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div className="bg-gray-50 p-2 rounded">Calcium: <strong>{pendingRecommendations.calcium_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Iron: <strong>{pendingRecommendations.iron_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Magnesium: <strong>{pendingRecommendations.magnesium_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Phosphorus: <strong>{pendingRecommendations.phosphorus_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Potassium: <strong>{pendingRecommendations.potassium_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Sodium: <strong>{pendingRecommendations.sodium_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Zinc: <strong>{pendingRecommendations.zinc_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Selenium: <strong>{pendingRecommendations.selenium_rec} mcg</strong></div>
                </div>
              </div>

              {/* Essential Fatty Acids & Amino Acids Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="mr-2">🥑</span> Essential Fatty Acids
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-gray-50 p-2 rounded">Omega-3: <strong>{pendingRecommendations.omega3_rec} g</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Omega-6: <strong>{pendingRecommendations.omega6_rec} g</strong></div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="mr-2">🧬</span> Essential Amino Acids
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div className="bg-gray-50 p-2 rounded">Lysine: <strong>{pendingRecommendations.lysine_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Leucine: <strong>{pendingRecommendations.leucine_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Isoleucine: <strong>{pendingRecommendations.isoleucine_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Phenylalanine: <strong>{pendingRecommendations.phenylalanine_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Methionine: <strong>{pendingRecommendations.methionine_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Valine: <strong>{pendingRecommendations.valine_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Threonine: <strong>{pendingRecommendations.threonine_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Tryptophan: <strong>{pendingRecommendations.tryptophan_rec} mg</strong></div>
                  <div className="bg-gray-50 p-2 rounded">Histidine: <strong>{pendingRecommendations.histidine_rec} mg</strong></div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Important:</strong> These recommendations will replace your current values. You can still manually adjust any nutrient after applying these calculations. Remember to click "Save Changes" at the bottom to save to the database.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelRecommendations}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  ❌ Cancel
                </button>
                <button
                  onClick={handleConfirmRecommendations}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  ✅ Apply Recommendations
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
